// src/components/ParcelManagement/DeliveryQueue.jsx  (or wherever you place it)
import React, { useState, useEffect } from 'react';
import { 
  RiMenuLine, 
  RiFilterLine, 
  RiRefreshLine, 
  RiNavigationLine, 
  RiPhoneLine, 
  RiCloseLine, 
  RiCheckLine, 
  RiLoaderLine 
} from 'react-icons/ri';

import './DeliveryQueue.css';
import { BASE_URL } from '../utils/constants';

const DeliveryQueue = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch real pending/in-progress deliveries
  useEffect(() => {
    const fetchDeliveries = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${BASE_URL}/api/orders/`);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);

        const data = await response.json();

        // Filter to active/pending deliveries only
        const active = data.filter(order => 
          ['pending', 'processing', 'shipped'].includes(order.deliveryStatus)
        );

        setDeliveries(active);
        setFilteredDeliveries(active);
      } catch (err) {
        console.error('Delivery queue error:', err);
        setError('Unable to load delivery queue.');
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveries();

    // Optional: refresh every 60 seconds
    const interval = setInterval(fetchDeliveries, 60000);
    return () => clearInterval(interval);
  }, []);

  // Search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredDeliveries(deliveries);
      return;
    }

    const term = searchTerm.toLowerCase();
    const results = deliveries.filter(order => 
      order.accountName?.toLowerCase().includes(term) ||
      order.phone?.toLowerCase().includes(term) ||
      order.deliveryAddress?.fullName?.toLowerCase().includes(term) ||
      order.deliveryAddress?.street?.toLowerCase().includes(term) ||
      order.reference?.toLowerCase().includes(term)
    );

    setFilteredDeliveries(results);
  }, [searchTerm, deliveries]);

  const handleRetry = (order) => {
    // TODO: PATCH to retry/reset status
    alert(`Retry requested for order ${order.reference}`);
    // Example: fetch(`/api/orders/${order.reference}/retry`, { method: 'PATCH' })
  };

  const handleStartDelivery = (order) => {
    // TODO: PATCH to change status to 'shipped' or assign courier
    alert(`Starting delivery for ${order.reference}`);
  };

  // const getStatusClass = (status) => {
  //   switch (status) {
  //     case 'delivered': return 'delivered';
  //     case 'shipped': return 'shipped';
  //     case 'processing': return 'processing';
  //     case 'pending': return 'pending';
  //     case 'failed': return 'failed';
  //     default: return 'pending';
  //   }
  // };

  if (loading) return <div className="loading"><RiLoaderLine className="spin" /> Loading queue...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="delivery-queue-page">
      <header className="mobile-header">
        <button className="menu-btn">
          <RiMenuLine size={22} />
        </button>
        <h1>Delivery Queue</h1>
        <div className="header-actions">
          <button className="action-icon" onClick={() => window.location.reload()}>
            <RiRefreshLine size={22} />
          </button>
          <button className="action-icon">
            <RiFilterLine size={22} />
          </button>
        </div>
      </header>

      <main className="queue-content">
        <div className="summary-bar">
          <div className="summary-item">
            <strong>{deliveries.length}</strong> remaining
          </div>
          <div className="summary-item">
            <strong>{deliveries.filter(d => d.deliveryStatus === 'processing' || d.deliveryStatus === 'shipped').length}</strong> in progress
          </div>
          <div className="summary-item">
            <strong>Today</strong>
          </div>
        </div>

        <div className="search-container">
          <RiCheckLine className="search-icon" size={20} />
          {/* <RiSearchLine className="search-icon" size={20} /> */}
          <input
            type="text"
            placeholder="Search by name, phone, address or reference..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="deliveries-list">
          {filteredDeliveries.length === 0 ? (
            <div className="empty-state">
              No pending deliveries {searchTerm ? `matching "${searchTerm}"` : 'at this time'}
            </div>
          ) : (
            filteredDeliveries.map(delivery => (
              <div
                key={delivery.reference}
                className={`delivery-card ${delivery.deliveryStatus === 'failed' ? 'failed' : ''}`}
              >
                <div className="delivery-header">
                  <h3 className="customer-name">
                    {delivery.deliveryAddress?.fullName || delivery.accountName || 'Unknown Customer'}
                  </h3>
                  {delivery.deliveryStatus === 'failed' && (
                    <span className="status-badge failed">
                      <RiCloseLine size={14} /> Failed
                    </span>
                  )}
                  {delivery.deliveryStatus === 'delivered' && (
                    <span className="status-badge delivered">
                      <RiCheckLine size={14} /> Delivered
                    </span>
                  )}
                </div>

                <p className="address-text">
                  {delivery.deliveryAddress?.street || 'N/A'}, {delivery.deliveryAddress?.city || ''}, {delivery.deliveryAddress?.province || ''}
                </p>

                <div className="delivery-meta">
                  <span className="meta-pill">
                    {delivery.estimatedDeliveryDate 
                      ? new Date(delivery.estimatedDeliveryDate).toLocaleDateString() 
                      : 'No date set'}
                  </span>
                  <span className="meta-pill">{delivery.items.length} packages</span>
                </div>

                {delivery.deliveryStatus === 'failed' && delivery.failedReason && (
                  <p className="failed-reason">{delivery.failedReason}</p>
                )}

                <div className="delivery-actions">
                  {delivery.deliveryStatus === 'failed' ? (
                    <button className="retry-btn" onClick={() => handleRetry(delivery)}>
                      Retry Delivery
                    </button>
                  ) : (
                    <button className="start-btn" onClick={() => handleStartDelivery(delivery)}>
                      Start Delivery
                    </button>
                  )}

                  <div className="icon-buttons">
                    <button className="icon-btn" title="Navigate">
                      <RiNavigationLine size={18} />
                    </button>
                    <button className="icon-btn" title="Call Customer">
                      <RiPhoneLine size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default DeliveryQueue;
// import React from 'react';
// import {
//   RiMenuLine,
//   RiFilterLine,
//   RiRefreshLine,          // already fixed previously
//   RiNavigationLine,
//   RiPhoneLine,
//   RiCloseLine,             // ← replaced RiXLine (does not exist) with this
//   RiCheckLine
// } from 'react-icons/ri';

// import './DeliveryQueue.css';

// const mockDeliveries = [
//   {
//     id: 1,
//     name: 'Anna Thompson',
//     address: '456 Oak Avenue, Eastside',
//     window: '11 AM - 3 PM',
//     packages: 3,
//     status: 'pending',
//   },
//   {
//     id: 2,
//     name: 'James Patel',
//     address: '321 Pine Road, Riverside',
//     window: '12 PM - 4 PM',
//     packages: 2,
//     status: 'pending',
//   },
//   {
//     id: 3,
//     name: 'Robert Kim',
//     address: '789 Cedar Lane, Northside',
//     window: '1 PM - 5 PM',
//     packages: 4,
//     status: 'failed',
//     failedReason: 'Customer not available - Retry scheduled',
//   },
//   {
//     id: 4,
//     name: 'Maria Gonzalez',
//     address: '147 Maple Street, Downtown',
//     window: '2 PM - 6 PM',
//     packages: 1,
//     status: 'pending',
//   },
// ];

// const DeliveryQueue = () => {
//   return (
//     <div className="delivery-queue-page">
//       <header className="mobile-header">
//         <button className="menu-btn">
//           <RiMenuLine size={22} />
//         </button>
//         <h1>Delivery Queue</h1>
//         <div className="header-actions">
//           <button className="action-icon">
//             <RiFilterLine size={22} />
//           </button>
//           <button className="action-icon">
//             <RiRefreshLine size={22} />
//           </button>
//         </div>
//       </header>

//       <main className="queue-content">
//         <div className="summary-bar">
//           <div className="summary-item">
//             <strong>8</strong> remaining
//           </div>
//           <div className="summary-item">
//             <strong>3</strong> in progress
//           </div>
//           <div className="summary-item">
//             <strong>Today</strong>
//           </div>
//         </div>

//         <div className="deliveries-list">
//           {mockDeliveries.map(delivery => (
//             <div
//               key={delivery.id}
//               className={`delivery-card ${delivery.status === 'failed' ? 'failed' : ''}`}
//             >
//               <div className="delivery-header">
//                 <h3 className="customer-name">{delivery.name}</h3>
//                 {delivery.status === 'failed' && (
//                   <span className="status-badge failed">
//                     <RiCloseLine size={14} /> Failed   {/* ← updated here */}
//                   </span>
//                 )}
//               </div>

//               <p className="address-text">{delivery.address}</p>

//               <div className="delivery-meta">
//                 <span className="meta-pill">{delivery.window}</span>
//                 <span className="meta-pill">{delivery.packages} packages</span>
//               </div>

//               {delivery.failedReason && (
//                 <p className="failed-reason">{delivery.failedReason}</p>
//               )}

//               <div className="delivery-actions">
//                 {delivery.status === 'failed' ? (
//                   <button className="retry-btn">
//                     Retry Delivery
//                   </button>
//                 ) : (
//                   <button className="start-btn">
//                     Start Delivery
//                   </button>
//                 )}

//                 <div className="icon-buttons">
//                   <button className="icon-btn">
//                     <RiNavigationLine size={18} />
//                   </button>
//                   <button className="icon-btn">
//                     <RiPhoneLine size={18} />
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </main>
//     </div>
//   );
// };

// export default DeliveryQueue;

// import React from 'react';
// import { RiMenuLine, RiFilterLine, RiRefreshCwLine, RiNavigationLine, RiPhoneLine, RiXLine, RiCheckLine } from 'react-icons/ri';
// import './DeliveryQueue.css';

// const mockDeliveries = [
//   {
//     id: 1,
//     name: 'Anna Thompson',
//     address: '456 Oak Avenue, Eastside',
//     window: '11 AM - 3 PM',
//     packages: 3,
//     status: 'pending',
//   },
//   {
//     id: 2,
//     name: 'James Patel',
//     address: '321 Pine Road, Riverside',
//     window: '12 PM - 4 PM',
//     packages: 2,
//     status: 'pending',
//   },
//   {
//     id: 3,
//     name: 'Robert Kim',
//     address: '789 Cedar Lane, Northside',
//     window: '1 PM - 5 PM',
//     packages: 4,
//     status: 'failed',
//     failedReason: 'Customer not available - Retry scheduled',
//   },
//   {
//     id: 4,
//     name: 'Maria Gonzalez',
//     address: '147 Maple Street, Downtown',
//     window: '2 PM - 6 PM',
//     packages: 1,
//     status: 'pending',
//   },
// ];

// const DeliveryQueue = () => {
//   return (
//     <div className="delivery-queue-page">
//       <header className="mobile-header">
//         <button className="menu-btn">
//           <RiMenuLine size={22} />
//         </button>
//         <h1>Delivery Queue</h1>
//         <div className="header-actions">
//           <button className="action-icon">
//             <RiFilterLine size={22} />
//           </button>
//           <button className="action-icon">
//             <RiRefreshCwLine size={22} />
//           </button>
//         </div>
//       </header>

//       <main className="queue-content">
//         <div className="summary-bar">
//           <div className="summary-item">
//             <strong>8</strong> remaining
//           </div>
//           <div className="summary-item">
//             <strong>3</strong> in progress
//           </div>
//           <div className="summary-item">
//             <strong>Today</strong>
//           </div>
//         </div>

//         <div className="deliveries-list">
//           {mockDeliveries.map(delivery => (
//             <div
//               key={delivery.id}
//               className={`delivery-card ${delivery.status === 'failed' ? 'failed' : ''}`}
//             >
//               <div className="delivery-header">
//                 <h3 className="customer-name">{delivery.name}</h3>
//                 {delivery.status === 'failed' && (
//                   <span className="status-badge failed">
//                     <RiXLine size={14} /> Failed
//                   </span>
//                 )}
//               </div>

//               <p className="address-text">{delivery.address}</p>

//               <div className="delivery-meta">
//                 <span className="meta-pill">{delivery.window}</span>
//                 <span className="meta-pill">{delivery.packages} packages</span>
//               </div>

//               {delivery.failedReason && (
//                 <p className="failed-reason">{delivery.failedReason}</p>
//               )}

//               <div className="delivery-actions">
//                 {delivery.status === 'failed' ? (
//                   <button className="retry-btn">
//                     Retry Delivery
//                   </button>
//                 ) : (
//                   <button className="start-btn">
//                     Start Delivery
//                   </button>
//                 )}

//                 <div className="icon-buttons">
//                   <button className="icon-btn">
//                     <RiNavigationLine size={18} />
//                   </button>
//                   <button className="icon-btn">
//                     <RiPhoneLine size={18} />
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </main>
//     </div>
//   );
// };

// export default DeliveryQueue;